import { useState, useEffect } from 'react';
import { SearchOutlined, FileTextOutlined, MessageOutlined, CalendarOutlined, UserOutlined, LoadingOutlined, InboxOutlined, PaperClipOutlined, DownloadOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReportByCaseId } from '../../hooks/useReports';
import { useReportAttachments, useDownloadAttachment } from '../../hooks/useAttachments';
import { formatDistanceToNow } from 'date-fns';
import { BerlixInput } from '../../components/common/BerlixInput';
import { MessageInput } from '../../components/messages';
import { useMessagesWithRealtime } from '../../hooks/useRealtimeMessages';
import './CaseLookupPage.css';
import './CaseLookupPage-responsive.css';

export default function CaseLookupPage() {
  const { t } = useTranslation();
  const { caseId: urlCaseId } = useParams();
  const navigate = useNavigate();
  const [searchCaseId, setSearchCaseId] = useState(urlCaseId || '');
  const [activeCaseId, setActiveCaseId] = useState(urlCaseId || '');
  
  // Use the hook with the active case ID
  const { data: reportData, isLoading, error, refetch } = useReportByCaseId(activeCaseId);
  
  // Fetch attachments if we have a report
  const { data: attachments } = useReportAttachments(reportData?.report?.id || '');
  const downloadAttachment = useDownloadAttachment();
  
  // Use realtime messages hook when we have a report
  const {
    messages: _realtimeMessages,
    sendMessage,
    loading: messagesLoading
  } = useMessagesWithRealtime(reportData?.report?.id || '');

  // Update search field when URL changes
  useEffect(() => {
    if (urlCaseId) {
      setSearchCaseId(urlCaseId);
      setActiveCaseId(urlCaseId);
    }
  }, [urlCaseId]);

  const handleSearch = () => {
    const trimmedValue = searchCaseId.trim().toUpperCase();
    if (trimmedValue) {
      setActiveCaseId(trimmedValue);
      navigate(`/case/${trimmedValue}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  const handleSendMessage = async (content: string) => {
    if (!reportData?.report?.id) {
      return { success: false, error: 'No report loaded' };
    }
    
    const result = await sendMessage(content, {
      isAdmin: false,
      senderName: reportData.report.reporter_alias || 'Anonymous',
      senderRole: 'reporter'
    });
    
    if (result.success) {
      // Refetch to update the static message display
      refetch();
    }
    
    return result;
  };

  const getStatusClass = (status: string) => {
    return `status-badge ${status.replace('_', '-')}`;
  };

  const getStatusText = (status: string) => {
    return t(`status.${status}`, { defaultValue: status.replace('_', ' ').toUpperCase() });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  const formatRelativeTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  return (
    <div className="case-lookup-container">
      <div className="case-lookup-card">
        <div className="lookup-header">
          <h1>{t('lookup.title', { defaultValue: 'Track Your Report' })}</h1>
          <p>{t('lookup.description', { defaultValue: 'Enter your case ID to view report status and messages' })}</p>
        </div>

        <div className="lookup-content">
          {/* Search Section */}
          <div className="search-section">
            <label className="search-label">
              {t('lookup.enterCaseId', { defaultValue: 'Enter Your Secure Case ID' })}
            </label>
            <div className="search-wrapper">
              <div className="search-input-group">
                <BerlixInput
                  icon={<SearchOutlined />}
                  placeholder="WB-COMPANY-2024-XXXXX"
                  value={searchCaseId}
                  onChange={(e) => setSearchCaseId(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="case-id-input-berlix"
                />
                <button 
                  className="search-button berlix-button"
                  onClick={handleSearch}
                  disabled={!searchCaseId.trim()}
                >
                  {t('lookup.lookupButton', { defaultValue: 'Look Up' })}
                </button>
              </div>
              <p className="search-hint">
                {t('lookup.caseIdHint', { defaultValue: 'Your case ID includes your company code and was provided when you submitted your report' })}
              </p>
              <p className="search-example">
                {t('lookup.caseIdExample', { defaultValue: 'Example: WB-TECH-2024-A3F2D or WB-HEALTH-2024-B7K9P' })}
              </p>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="loading-container">
              <LoadingOutlined className="loading-spinner" />
              <p className="loading-text">{t('common.loading', { defaultValue: 'Loading report details...' })}</p>
            </div>
          )}

          {/* Error State */}
          {error && activeCaseId && !isLoading && (
            <div className="error-alert">
              <div className="error-alert-title">
                {t('lookup.notFound', { defaultValue: 'Report Not Found' })}
              </div>
              <div className="error-alert-description">
                {t('lookup.notFoundDescription', { 
                  defaultValue: `No report found with case ID: ${activeCaseId}. Please check your case ID and try again.`,
                  caseId: activeCaseId 
                })}
              </div>
            </div>
          )}

          {/* Report Details */}
          {reportData?.success && reportData.report && (
            <>
              {/* Report Header */}
              <div className="report-card">
                <div className="report-header">
                  <div className="report-title-section">
                    <h2>
                      <FileTextOutlined />
                      {t('lookup.reportDetails', { defaultValue: 'Report Details' })}
                    </h2>
                    <div className="case-id-display">
                      <span className="case-id-label">{t('lookup.caseId', { defaultValue: 'Case ID' })}:</span>
                      <span className="case-id-value">{reportData.report.case_id}</span>
                    </div>
                  </div>
                  <div className={getStatusClass(reportData.report.status)}>
                    {getStatusText(reportData.report.status)}
                  </div>
                </div>

                <div className="report-meta">
                  <div className="meta-item">
                    <CalendarOutlined />
                    <strong>{t('lookup.submittedOn', { defaultValue: 'Submitted' })}:</strong>
                    <span>{formatDate(reportData.report.created_at)}</span>
                    <span style={{ color: '#94a3b8' }}>({formatRelativeTime(reportData.report.created_at)})</span>
                  </div>

                  {reportData.report.updated_at !== reportData.report.created_at && (
                    <div className="meta-item">
                      <CalendarOutlined />
                      <strong>{t('lookup.lastUpdated', { defaultValue: 'Last Updated' })}:</strong>
                      <span>{formatDate(reportData.report.updated_at)}</span>
                      <span style={{ color: '#94a3b8' }}>({formatRelativeTime(reportData.report.updated_at)})</span>
                    </div>
                  )}

                  <div className="meta-item">
                    <UserOutlined />
                    <strong>{t('report.reporterAlias', { defaultValue: 'Reporter' })}:</strong>
                    <span>{reportData.report.reporter_alias || t('common.anonymous', { defaultValue: 'Anonymous' })}</span>
                  </div>

                  <div className="meta-item">
                    <strong>{t('report.category', { defaultValue: 'Category' })}:</strong>
                    <span>{t(`categories.${reportData.report.category}`, { defaultValue: reportData.report.category })}</span>
                  </div>

                  {reportData.report.priority && (
                    <div className="meta-item">
                      <strong>{t('report.priority', { defaultValue: 'Priority' })}:</strong>
                      <span style={{ 
                        color: reportData.report.priority === 'critical' ? '#dc2626' :
                               reportData.report.priority === 'high' ? '#ea580c' :
                               reportData.report.priority === 'medium' ? '#2563eb' : '#64748b',
                        fontWeight: 600
                      }}>
                        {reportData.report.priority.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                <div className="report-content-section">
                  <label className="content-label">{t('report.content', { defaultValue: 'Report Content' })}:</label>
                  <div className="content-box">
                    {reportData.report.content}
                  </div>
                </div>

                {/* Attachments Section */}
                {attachments && attachments.length > 0 && (
                  <div className="attachments-section">
                    <label className="content-label">
                      <PaperClipOutlined className="mr-2" />
                      {t('report.attachments', { defaultValue: 'Attachments' })} ({attachments.length})
                    </label>
                    <div className="attachments-list">
                      {attachments.map((attachment) => (
                        <div 
                          key={attachment.id} 
                          className="attachment-item"
                          onClick={() => downloadAttachment.mutate(attachment.id)}
                        >
                          <FileTextOutlined className="attachment-icon" />
                          <div className="attachment-info">
                            <div className="attachment-name">{attachment.file_name}</div>
                            <div className="attachment-meta">
                              {attachment.file_size ? `${(attachment.file_size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                            </div>
                          </div>
                          <DownloadOutlined className="download-icon" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Messages Section */}
              <div className="messages-card">
                <h3 className="messages-header">
                  <MessageOutlined />
                  {t('lookup.messages', { defaultValue: 'Communication Thread' })}
                </h3>

                {reportData.messages && reportData.messages.length > 0 ? (
                  <div className="message-timeline">
                    {reportData.messages.map((message, index) => (
                      <div key={index} className={`message-item ${message.is_admin_message ? 'admin' : ''}`}>
                        <div className="message-dot" />
                        <div className={`message-card ${message.is_admin_message ? 'admin' : ''}`}>
                          <div className="message-header">
                            <span className="message-sender">
                              {message.is_admin_message ? 
                                t('lookup.adminResponse', { defaultValue: 'Case Manager' }) : 
                                t('lookup.yourMessage', { defaultValue: 'Your Message' })
                              }
                            </span>
                            <span className="message-time">
                              {formatRelativeTime(message.created_at)}
                            </span>
                          </div>
                          <div className="message-content">
                            {message.content}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <InboxOutlined className="empty-icon" />
                    <p className="empty-text">{t('lookup.noMessages', { defaultValue: 'No messages yet' })}</p>
                    <p className="empty-hint">{t('lookup.noMessagesHint', { defaultValue: 'Check back later for updates from the case manager' })}</p>
                  </div>
                )}

                {/* Message Input */}
                <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                  <MessageInput
                    onSend={handleSendMessage}
                    placeholder={t('lookup.typeMessage', { defaultValue: 'Type your message to the case manager...' })}
                    loading={messagesLoading}
                    allowAttachments={false}
                  />
                </div>

                <div className="info-alert">
                  <span className="info-alert-icon">ℹ️</span>
                  <div>
                    <div className="info-alert-title">
                      {t('lookup.messagingInfo', { defaultValue: 'Secure Communication' })}
                    </div>
                    <div className="info-alert-description">
                      {t('lookup.messagingDescription', { 
                        defaultValue: 'All messages are encrypted end-to-end. You can communicate with case managers while maintaining complete anonymity.' 
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Empty State */}
          {!activeCaseId && !isLoading && (
            <div className="empty-state">
              <FileTextOutlined className="empty-icon" />
              <p className="empty-text">{t('lookup.noReportSelected', { defaultValue: 'No Report Selected' })}</p>
              <p className="empty-hint">{t('lookup.enterCaseIdPrompt', { defaultValue: 'Enter a case ID above to view report details' })}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}